import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  getStoreByOwner, 
  getStoreBanners,
  createStoreBanner,
  updateStoreBanner,
  deleteStoreBanner
} from '../lib/firebaseService';
import ImageUpload from '../components/ImageUpload';
import { Trash2 } from 'lucide-react';

export default function StoreBanner() {
  const { firebaseUser } = useAuth();
  const [store, setStore] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  useEffect(() => {
    if (firebaseUser) {
      loadData();
    }
  }, [firebaseUser]);

  const loadData = async () => {
    try {
      setPageLoading(true);
      const userStore = await getStoreByOwner(firebaseUser.uid);
      if (userStore) {
        setStore(userStore);
        const storeBanners = await getStoreBanners(userStore.id);
        setBanners(storeBanners);
      }
    } catch (error) {
      console.error('Məlumatlar yüklənə bilmədi:', error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleAddBanner = async (imageUrl) => {
    if (!store) {
      toast.error('Əvvəlcə mağaza yaradın');
      return;
    }

    if (!imageUrl) return;

    try {
      setUploadingBanner(true);
      
      if (banners.length > 0) {
        await updateStoreBanner(banners[0].id, { image_url: imageUrl });
        toast.success('Arxa fon yeniləndi');
      } else {
        await createStoreBanner({
          store_id: store.id,
          image_url: imageUrl,
          order: 0
        });
        toast.success('Arxa fon əlavə edildi');
      }
      
      loadData();
    } catch (error) {
      toast.error('Arxa fon əlavə edilə bilmədi');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Bu arxa fon şəklini silmək istədiyinizə əminsiniz?')) return;

    try {
      await deleteStoreBanner(bannerId);
      toast.success('Arxa fon silindi');
      loadData();
    } catch (error) {
      toast.error('Arxa fon silinə bilmədi');
    }
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!store) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl border border-border p-8 text-center">
            <p className="text-muted-foreground mb-4">Əvvəlcə mağaza yaradın</p>
            <Button onClick={() => window.location.href = '/dashboard/store'}>
              Mağaza Yarat
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Arxa Fon Şəkli</h1>
          <p className="text-muted-foreground mt-1">Mağazanızın üst hissəsində görünəcək arxa fon</p>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <h2 className="text-xl font-semibold mb-2">WhatsApp Business Stili</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Arxa fon şəkli profil şəklinin arxasında cover photo kimi göstəriləcək
          </p>
          
          <div className="mb-6">
            <Label>Arxa Fon Şəkli {banners.length > 0 ? '(Mövcud şəkli dəyişdirmək)' : '(Yeni şəkil əlavə et)'}</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Tövsiyə olunan ölçü: 800x200px və ya daha böyük (16:4 nisbəti)
            </p>
            <ImageUpload
              currentImage={banners.length > 0 ? banners[0].image_url : ""}
              onUploadComplete={handleAddBanner}
              label="Arxa fon şəklini yüklə"
            />
          </div>

          {banners.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold">Cari Arxa Fon</h3>
              <div className="border border-border rounded-lg overflow-hidden">
                <img
                  src={banners[0].image_url}
                  alt="Arxa fon"
                  className="w-full h-40 object-cover"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/800x200?text=Arxa+Fon'}
                />
                <div className="p-4 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Yuxarıdakı yükləyicidən yeni şəkil seçərək dəyişdirin</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    onClick={() => handleDeleteBanner(banners[0].id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Sil
                  </Button>
                </div>
              </div>
            </div>
          )}

          {banners.length === 0 && (
            <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg">
              Arxa fon şəkli yoxdur. Yuxarıdakı yükləyicidən əlavə edin.
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
